import React from "react";
import { Box, Container, CssBaseline, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import NavBar from "./components/NavBar";
import Home from "./components/Home";
import { createAppTheme } from "./theme";

const STORAGE_KEY = "protext-color-mode";

function getInitialMode(): "light" | "dark" {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  } catch {
    /* localStorage unavailable — fall through to light */
  }
  return "light";
}

function App() {
  const [mode, setMode] = React.useState<"light" | "dark">(getInitialMode);
  const [rawText, setRawText] = React.useState("");
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  const toggleMode = () =>
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore persistence errors */
      }
      return next;
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        <NavBar mode={mode} onToggleMode={toggleMode} />

        <Box component="main" sx={{ flex: 1, width: "100%" }}>
          <Home rawText={rawText} setRawText={setRawText} />
        </Box>

        <Box
          component="footer"
          sx={{ borderTop: 1, borderColor: "divider", py: 2, mt: 4 }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              ProText — Developed by Caleb Tony-Enwin
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
